import { useCallback, useEffect, useRef } from 'react';

/**
 * useTableRange
 *
 * Calculates the currently visible row range (from–to) in a scrollable table
 * based on scroll position, viewport height, and row density.
 *
 /**
 * Core logic for calculating visible table rows.
 *
 * updateRange:
 * - Reads current scroll position (scrollTop) and container height (clientHeight)
 * - Calculates how many rows can fit into the visible viewport
 * - Determines the first (`from`) and last (`to`) visible row indexes
 *
 * Steps:
 * 1. Get scroll position:
 *    - scrollTop represents how far the user has scrolled vertically
 *
 * 2. Calculate viewport height:
 *    - clientHeight is the visible container height
 *    - adjusted by rowDensityHeight to exclude non-row space
 *
 * 3. Detect bottom edge:
 *    - When user scrolls to the end, normal math can produce incorrect values
 *    - `isAtBottom` ensures the last rows are calculated correctly
 *
 * 4. Calculate visible rows:
 *    - visibleCount = how many rows fit into viewport
 *
 * 5. Compute range:
 *    - If at bottom:
 *        → show last rows (avoid empty space / ghost rows)
 *    - Otherwise:
 *        → calculate based on scroll position
 *
 * 6. Clamp values:
 *    - Ensures `from` and `to` never exceed dataset bounds
 *    - Prevents invalid states like "17 of 2"
 *
 * 7. Prevent unnecessary updates:
 *    - Uses `prev` ref to avoid triggering onChange if range hasn't changed
 *
 * 8. Emit result:
 *    - Converts 0-based indexes → 1-based for UI display
 *    - Calls onChange({ from, to, total })
 *
 *
 * Scroll effect:
 * - Listens to scroll events on the container
 * - Uses `requestAnimationFrame` + `ticking` flag to throttle updates
 * - Ensures updateRange runs at most once per frame (~60fps)
 * - Prevents performance issues from frequent scroll events
 *
 *
 * Resize effect:
 * - Uses ResizeObserver to detect container size changes
 * - Recalculates visible range when layout or viewport changes
 * - Ensures correct values on responsive layouts and mobile devices
 *
 *
 * Init effect:
 * - Runs updateRange on mount and when dependencies change
 * - Ensures correct initial range before any scroll happens
 *
 *
 * Overall behavior:
 * - Keeps visible row range in sync with:
 *   → scroll position
 *   → container size
 *   → data length
 *   → row density
 *
 * - Optimized for performance and avoids unnecessary re-renders
 * 
 */

type Params = {
  parentRef: React.RefObject<HTMLDivElement>;
  rowDensityHeight: number;
  dataLength: number;
  onChange: (range: { from: number; to: number; total: number }) => void;
};

export const useTableRange = ({ parentRef, rowDensityHeight, dataLength, onChange }: Params) => {
  const prev = useRef({ from: 0, to: 0 });

  const updateRange = useCallback(() => {
    const container = parentRef.current;
    if (!container) return;

    if (dataLength === 0) {
      prev.current = { from: -1, to: -1 };

      onChange({
        from: 0,
        to: 0,
        total: 0,
      });

      return;
    }
    const scrollTop = container.scrollTop;
    const viewportHeight = container.clientHeight - 2 * rowDensityHeight;
    const maxScroll = container.scrollHeight - container.clientHeight;
    const visibleCount = Math.floor(viewportHeight / rowDensityHeight);
    const isAtBottom = scrollTop >= maxScroll - 1;

    let from: number;
    let to: number;

    if (isAtBottom) {
      from = Math.max(0, dataLength - visibleCount);
      to = dataLength - 1;
    } else {
      from = Math.floor(scrollTop / rowDensityHeight);
      to = Math.floor((scrollTop + viewportHeight - 1) / rowDensityHeight);
    }

    from = Math.min(from, dataLength - 1);
    to = Math.min(to, dataLength - 1);

    to = Math.max(from, to);

    if (prev.current.from !== from || prev.current.to !== to) {
      prev.current = { from, to };

      onChange({
        from: from + 1,
        to: to + 1,
        total: dataLength,
      });
    }
  }, [parentRef, rowDensityHeight, dataLength, onChange]);

  // scroll
  useEffect(() => {
    const container = parentRef.current;
    if (!container) return;

    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateRange();
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line
  }, [rowDensityHeight, dataLength]);

  // resize
  useEffect(() => {
    const container = parentRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(updateRange);
    });

    observer.observe(container);

    return () => observer.disconnect();
    // eslint-disable-next-line
  }, [rowDensityHeight, dataLength]);

  // init
  useEffect(() => {
    prev.current = { from: -1, to: -1 };
    updateRange();

    // eslint-disable-next-line
  }, [rowDensityHeight, dataLength]);
};
